/**
 * 배송비 "범위반복" — 고도몰 배송조건 `rangeRepeat` 이관 (order-service V61).
 *
 * 첫 구간(가장 낮은 구간) 요금 P0 에, 측정값이 `start` 이상이면 `unit` 을 넘을 때마다 `fee` 를 더한다.
 *
 *   측정값 < start  →  P0
 *   측정값 ≥ start  →  P0 + k × fee,  k = ⌊(측정값 − start) ÷ unit⌋ + 1
 *
 * 예) P0 20,000 · start 0.5kg · unit 1kg · fee 10,000 → ~0.5kg 20,000, 0.5~1.5kg 30,000, 1.5~2.5kg 40,000.
 *
 * 해외 국가그룹(단위 kg)과 국내 배송비조건(BY_AMOUNT 원 / BY_QTY 개 / BY_WEIGHT kg)이 같은 규칙을 쓴다.
 * 계산식·검증은 be-order `RangeRepeat` 과 같아야 한다 — 이 파일이 BO 의 유일한 사본이다.
 */

/** API 형태 — 해외 `countryGroups[].rangeRepeat`, 국내 배송비조건 최상위 `rangeRepeat`. null = 반복 없음. */
export interface RangeRepeat {
  /** 시작값. 0 이상. */
  start: number;
  /** 반복 단위. 0 보다 큼. */
  unit: number;
  /** 단위당 추가요금(원). 0 이상 정수. */
  fee: number;
}

/** 어느 값이 BE 검증에 걸리는지. null 이면 통과. */
export type RangeRepeatProblem = 'start' | 'unit' | 'fee';

/**
 * be-order `RangeRepeatPayload`(@NotNull, start @PositiveOrZero, unit @Positive, fee @PositiveOrZero Long)
 * 와 같은 기준으로 검사한다. 걸리는 첫 항목을 돌려준다.
 */
export const validateRangeRepeat = (r: RangeRepeat): RangeRepeatProblem | null => {
  if (!Number.isFinite(r.start) || r.start < 0) return 'start';
  if (!Number.isFinite(r.unit) || r.unit <= 0) return 'unit';
  if (!Number.isFinite(r.fee) || r.fee < 0 || !Number.isInteger(r.fee)) return 'fee';
  return null;
};

/**
 * 측정값에서의 요금. kg 소수 경계(딱 나누어떨어지는 값)에서 부동소수 오차로 한 칸 어긋나지 않도록
 * 아주 작은 여유를 두고 내림한다 — BE 는 BigDecimal 로 정확히 계산한다.
 */
export const rangeRepeatFeeAt = (baseFee: number, r: RangeRepeat, measure: number): number => {
  const n = measure - r.start;
  if (n < 0) return baseFee;
  const k = Math.floor(n / r.unit + 1e-9) + 1;
  return baseFee + k * r.fee;
};

/** 켰을 때의 기본값 — 첫 구간이 끝나는 지점부터 1 단위마다(예시와 같은 출발점). */
export const defaultRangeRepeat = (firstUpperBound: number | null | undefined): RangeRepeat => ({
  start: firstUpperBound != null && Number.isFinite(firstUpperBound) && firstUpperBound > 0 ? firstUpperBound : 0,
  unit: 1,
  fee: 0,
});

const trimMeasure = (n: number): string =>
  Number(n.toFixed(4)).toLocaleString('ko-KR', { maximumFractionDigits: 4 });

const won = (n: number): string => `${n.toLocaleString('ko-KR')}원`;

/**
 * 미리보기 문구 — "~0.5kg 20,000원 · 0.5~1.5kg 30,000원 · 1.5~2.5kg 40,000원 …".
 * 첫 구간 뒤로 `steps` 칸을 보여준다. 검증에 걸리는 값이면 빈 문자열(잘못된 미리보기를 보이지 않는다).
 */
export const rangeRepeatPreview = (
  baseFee: number,
  r: RangeRepeat,
  unitLabel: string,
  steps = 3,
): string => {
  if (validateRangeRepeat(r) !== null || !Number.isFinite(baseFee)) return '';
  const parts = [`~${trimMeasure(r.start)}${unitLabel} ${won(baseFee)}`];
  for (let i = 1; i <= steps; i += 1) {
    const from = r.start + (i - 1) * r.unit;
    const to = r.start + i * r.unit;
    parts.push(`${trimMeasure(from)}~${trimMeasure(to)}${unitLabel} ${won(baseFee + i * r.fee)}`);
  }
  return `${parts.join(' · ')} …`;
};
